import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Settings as SettingsIcon, Shield, Crown, Menu, Users, Plus, User, MessageCircle, CreditCard, Calendar, CheckCircle, Heart, Zap, Check, Coins, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "@/hooks/useTranslation";

import { SubscriptionPaymentModal } from "@/components/SubscriptionPaymentModal";
import { apiClient } from "@/api/ApiClient";
import { useToast } from "@/hooks/use-toast";

interface SettingsProps {
  onNavigateToChat: () => void;
  onNavigateToMyBoyfriends: () => void;
  onNavigateToProfile: () => void;
  onCreateNew: () => void;
  onNavigateToPrivacySettings: () => void;
  onNavigateToSubscriptionSettings: () => void;
  onNavigateToNotificationSettings: () => void;
  onNavigateToExplore: () => void;
  onNavigateToPremium: () => void;
  user?: {
    email?: string;
    name?: string;
    isPremium?: boolean;
    subscriptionTier?: string;
    subscriptionEnd?: string;
  };
  onTokensClick?: () => void;
  userTokens?: number;
  onPurchaseTokens?: (tokens: number, paymentMethod: string) => void;
  onUpgradeToPremium?: () => void;
}

export const Settings = ({ onNavigateToChat, onNavigateToMyBoyfriends, onNavigateToProfile, onCreateNew, onNavigateToPrivacySettings, onNavigateToSubscriptionSettings, onNavigateToNotificationSettings, onNavigateToExplore, onNavigateToPremium, user, onTokensClick, userTokens, onPurchaseTokens, onUpgradeToPremium }: SettingsProps) => {
  const { t } = useTranslation();
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showSubscriptionPaymentModal, setShowSubscriptionPaymentModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentFormModal, setShowPaymentFormModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedTokenPackage, setSelectedTokenPackage] = useState<'small' | 'medium' | 'large'>('medium');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'crypto'>('card');
  const { toast } = useToast();

  const handleCompletePayment = () => {
    // Handle payment completion
    const tokenAmount = selectedTokenPackage === 'small' ? 100 : selectedTokenPackage === 'medium' ? 250 : 500;
    const price = selectedTokenPackage === 'small' ? '4.99' : selectedTokenPackage === 'medium' ? '9.99' : '17.99';
    
    // Actually update the tokens using the callback
    if (onPurchaseTokens) {
      onPurchaseTokens(tokenAmount, selectedPaymentMethod);
    }
    
    setShowPaymentFormModal(false);
    // Note: The toast will be shown by the parent component's handlePurchaseTokens function
  };

  const handleManageSubscription = async () => {
    try {
      const data = await apiClient.post('/billing/customer-portal');
      
      // Open Stripe customer portal in new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      // Silent error - no notification
    }
  };

  const handleUpgradePlan = () => {
    setShowPremiumModal(true);
  };

  const handleCancelSubscription = async () => {
    try {
      const data = await apiClient.post('/billing/customer-portal');
      
      // Open Stripe customer portal where user can cancel
      window.open(data.url, '_blank');
      
      // Silent operation - no notification
    } catch (error) {
      console.error('Error opening customer portal:', error);
      // Silent error - no notification
    }
  };

  const handlePurchaseSubscription = (plan: 'monthly' | 'yearly', paymentMethod: string) => {
    if (onUpgradeToPremium) {
      onUpgradeToPremium();
    }
    // Silent upgrade - no notification
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const settingsCategories = [
    {
      title: t('settings.account'),
      icon: Shield,
      items: [
        ...(user?.isPremium ? [{ name: t('settings.subscription'), description: t('settings.manage_subscription'), action: onNavigateToSubscriptionSettings }] : []),
      ],
    },
  ];

  const handleToggle = (category: string, item: string) => {
    // Handle toggle logic here
    console.log(`Toggling ${item} in ${category}`);
  };

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Main Content */}
      <div className="p-4 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4 shrink-0" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
            <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
              {t('settings.title')}
            </h1>
          </div>

        {/* Membership Section for Premium Users */}
        {user?.isPremium && (
          <Card className="p-6 mb-6 border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="h-6 w-6 text-primary" />
              <h3 className="text-lg font-semibold">{t('settings.my_membership')}</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                  <span className="font-medium">{t('settings.plan')}</span>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-primary font-semibold">
                      {user.subscriptionTier || "Premium"}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                  <span className="font-medium">{t('settings.status')}</span>
                  <span className="text-green-600 font-medium">{t('settings.active')}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                  <span className="font-medium">{t('settings.billing_email')}</span>
                  <span className="text-muted-foreground text-sm">{user.email}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                  <span className="font-medium">{t('settings.next_billing')}</span>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground text-sm">
                      {formatDate(user.subscriptionEnd)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                  <span className="font-medium">{t('settings.payment_method')}</span>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground text-sm">•••• 4242</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg">
                  <span className="font-medium">{t('settings.monthly_cost')}</span>
                  <span className="font-semibold">$9.99</span>
                </div>
              </div>
            </div>
            
            {/* Token Information */}
            <div className="border-t border-primary/20 pt-4">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full">
                    <Coins className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800 dark:text-amber-200">{t('settings.tokens')}</h4>
                    <p className="text-sm text-amber-700 dark:text-amber-300">{t('settings.tokens_remaining', { count: user?.isPremium ? userTokens || 0 : 0 })}</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowTokenModal(true)}
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-amber-400 hover:from-amber-600 hover:to-yellow-600 hover:shadow-lg transition-all duration-200 hover:scale-105"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('settings.add_tokens')}
                </Button>
              </div>
            </div>
          </Card>
        )}

         {/* Language Settings */}
         <Card className="p-6 mb-6 border-primary/20">
           <LanguageSwitcher />
         </Card>

         {/* Settings Categories */}
         <div className="space-y-6">
           {settingsCategories.map((category) => (
             <Card key={category.title} className="p-6 border-primary/20">
               <div className="flex items-center gap-2 mb-4">
                 <category.icon className="h-5 w-5" />
                 <h3 className="font-semibold">{category.title}</h3>
               </div>
               
               <div className="space-y-3">
                 {category.items.map((item) => (
                   <div key={item.name} className="flex items-center justify-between p-3 rounded-lg bg-card/50 hover:bg-card/70 transition-colors">
                     <div className="flex-1">
                       <h4 className="font-medium">{item.name}</h4>
                       <p className="text-sm text-muted-foreground">
                         {item.description}
                       </p>
                     </div>
                     
                     <div className="ml-4">
                       <Button variant="ghost" size="sm" onClick={item.action}>
                         →
                       </Button>
                     </div>
                   </div>
                 ))}
               </div>
             </Card>
           ))}
         </div>

        </div>
      </div>

      {/* Premium Modal */}
      <Dialog open={showPremiumModal} onOpenChange={setShowPremiumModal}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none overflow-y-auto">`
          <DialogHeader>
            <DialogTitle className="text-center text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              {t('premium_modal.upgrade_romance')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Urgency Banner */}
            <div className="border-2 border-orange-500 bg-transparent text-orange-500 p-3 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2">
                <span className="text-lg">⏰</span>
                <span className="font-bold text-sm">{t('premium_modal.limited_time_offer')}</span>
              </div>
            </div>

            {/* Features Section */}
            <Card className="p-4 bg-gradient-romantic text-white border-0">
              <div className="text-center mb-4">
                <Zap className="h-8 w-8 mx-auto mb-2" />
                <h2 className="text-lg font-bold mb-2">{t('premium_modal.premium_features')}</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.unblurred_photos')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.hd_images')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.priority_responses')}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.free_tokens')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.generate_spicy')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">{t('premium_modal.unlock_premium_bfs')}</span>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Pricing Plans */}
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className={`p-3 text-center border-2 cursor-pointer transition-all hover:scale-105 ${
                  selectedPlan === 'monthly' 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPlan('monthly')}
              >
                <h3 className="text-lg font-bold mb-2">{t('premium_modal.monthly')}</h3>
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground line-through">$19.99</p>
                  <p className="text-2xl font-bold text-primary">$9.99</p>
                  <p className="text-xs text-muted-foreground">{t('premium_modal.per_month')}</p>
                </div>
                <p className="text-xs font-medium text-green-600 mb-2">{t('premium_modal.save_50')}</p>
                <ul className="text-[10px] text-muted-foreground space-y-1 mb-3">
                  <li>{t('premium_modal.all_premium_features')}</li>
                  <li>{t('premium_modal.cancel_anytime')}</li>
                  <li>{t('premium_modal.free_trial')}</li>
                </ul>
                {selectedPlan === 'monthly' && (
                  <div className="text-xs text-primary font-medium">{t('premium_modal.selected')}</div>
                )}
              </Card>

              <Card 
                className={`p-3 text-center border-2 cursor-pointer transition-all hover:scale-105 relative ${
                  selectedPlan === 'yearly' 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20 shadow-lg' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPlan('yearly')}
              >
                <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                  <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">
                    {t('premium_modal.best_value')}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-2">{t('premium_modal.yearly')}</h3>
                <div className="mb-2">
                  <p className="text-xs text-muted-foreground line-through">$159.99</p>
                  <p className="text-2xl font-bold text-primary">$79.99</p>
                  <p className="text-xs text-muted-foreground">{t('premium_modal.per_year')}</p>
                </div>
                <p className="text-xs font-medium text-green-600 mb-2">{t('premium_modal.save_50_plus')}</p>
                <ul className="text-[10px] text-muted-foreground space-y-1 mb-3">
                  <li>{t('premium_modal.all_premium_features')}</li>
                  <li>{t('premium_modal.two_months_free')}</li>
                  <li>{t('premium_modal.priority_support')}</li>
                </ul>
                {selectedPlan === 'yearly' && (
                  <div className="text-xs text-primary font-medium">{t('premium_modal.selected')}</div>
                )}
              </Card>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button 
                variant="romantic" 
                size="lg" 
                className="w-full py-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={() => {
                  setShowPremiumModal(false);
                  setShowSubscriptionPaymentModal(true);
                }}
              >
                <Crown className="h-4 w-4 mr-2" />
                {t('premium_modal.upgrade_to_plan', { plan: selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan') })}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                {t('premium_modal.secure_payment')}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Token Purchase Modal */}
      <Dialog open={showTokenModal} onOpenChange={setShowTokenModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-xl">
              <Zap className="h-6 w-6 text-amber-500" />
              {t('settings.add_tokens')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('settings.choose_token_package')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Token Packages */}
            <div className="space-y-3">
              <Card 
                className={`p-4 cursor-pointer transition-all border-2 ${
                  selectedTokenPackage === 'small' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedTokenPackage('small')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{t('settings.tokens_100')}</h3>
                    <p className="text-sm text-muted-foreground">{t('settings.perfect_casual')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">$4.99</p>
                    <p className="text-xs text-muted-foreground">$0.05 {t('settings.per_token')}</p>
                  </div>
                </div>
              </Card>

              <Card 
                className={`p-4 cursor-pointer transition-all border-2 relative ${
                  selectedTokenPackage === 'medium' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedTokenPackage('medium')}
              >
                <div className="absolute -top-1 right-2">
                  <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                    {t('settings.popular')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{t('settings.tokens_250')}</h3>
                    <p className="text-sm text-muted-foreground">{t('settings.best_value_regular')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">$9.99</p>
                    <p className="text-xs text-muted-foreground">$0.04 {t('settings.per_token')}</p>
                  </div>
                </div>
              </Card>

              <Card 
                className={`p-4 cursor-pointer transition-all border-2 ${
                  selectedTokenPackage === 'large' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedTokenPackage('large')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{t('settings.tokens_500')}</h3>
                    <p className="text-sm text-muted-foreground">{t('settings.maximum_savings')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">$17.99</p>
                    <p className="text-xs text-muted-foreground">$0.036 {t('settings.per_token')}</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="flex flex-col gap-2">
              <Button 
                variant="romantic" 
                className="w-full"
                onClick={() => {
                  setShowTokenModal(false);
                  setShowPaymentModal(true);
                }}
              >
                <Zap className="h-4 w-4 mr-2" />
                {t('settings.purchase_tokens', { count: selectedTokenPackage === 'small' ? '100' : selectedTokenPackage === 'medium' ? '250' : '500' })}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Method Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-xl">
              <Zap className="h-6 w-6 text-amber-500" />
              {t('settings.choose_payment_method')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('settings.payment_method_description', { 
                count: selectedTokenPackage === 'small' ? '100' : selectedTokenPackage === 'medium' ? '250' : '500',
                price: selectedTokenPackage === 'small' ? '4.99' : selectedTokenPackage === 'medium' ? '9.99' : '17.99'
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Payment Method Selection */}
            <div className="space-y-3">
              <Card 
                className={`p-4 cursor-pointer transition-all border-2 ${
                  selectedPaymentMethod === 'card' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPaymentMethod('card')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                    <span className="text-white text-sm font-bold">💳</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('settings.credit_debit_card')}</h3>
                    <p className="text-sm text-muted-foreground">{t('settings.pay_with_cards')}</p>
                  </div>
                </div>
              </Card>

              <Card 
                className={`p-4 cursor-pointer transition-all border-2 ${
                  selectedPaymentMethod === 'crypto' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-primary/20 hover:border-primary/40'
                }`}
                onClick={() => setSelectedPaymentMethod('crypto')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-500 rounded flex items-center justify-center">
                    <span className="text-white text-sm font-bold">₿</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('settings.cryptocurrency')}</h3>
                    <p className="text-sm text-muted-foreground">{t('settings.pay_with_crypto')}</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="flex flex-col gap-2">
              <Button 
                variant="romantic" 
                className="w-full"
                onClick={() => {
                  setShowPaymentModal(false);
                  setShowPaymentFormModal(true);
                }}
              >
                <span className="mr-2">{selectedPaymentMethod === 'card' ? '💳' : '₿'}</span>
                {t('settings.continue_with_payment', { method: selectedPaymentMethod === 'card' ? t('settings.card') : t('settings.crypto') })}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowPaymentModal(false);
                setShowTokenModal(true);
              }}>
                {t('settings.back_to_token_selection')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Form Modal */}
      <Dialog open={showPaymentFormModal} onOpenChange={setShowPaymentFormModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-xl">
              <span className="text-2xl">{selectedPaymentMethod === 'card' ? '💳' : '₿'}</span>
              {selectedPaymentMethod === 'card' ? t('settings.card_payment') : t('settings.crypto_payment')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('settings.complete_payment_description', { 
                count: selectedTokenPackage === 'small' ? '100' : selectedTokenPackage === 'medium' ? '250' : '500',
                price: selectedTokenPackage === 'small' ? '4.99' : selectedTokenPackage === 'medium' ? '9.99' : '17.99'
              })}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedPaymentMethod === 'card' ? (
              // Card Payment Form
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Card Number</label>
                  <input 
                    type="text" 
                    placeholder="1234 5678 9012 3456"
                    className="w-full p-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Expiry Date</label>
                    <input 
                      type="text" 
                      placeholder="MM/YY"
                      className="w-full p-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">CVV</label>
                    <input 
                      type="text" 
                      placeholder="123"
                      className="w-full p-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cardholder Name</label>
                  <input 
                    type="text" 
                    placeholder="John Doe"
                    className="w-full p-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            ) : (
              // Crypto Payment Form
              <div className="space-y-4">
                <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <div className="text-center space-y-2">
                    <div className="text-lg font-semibold">{t('settings.send_payment_to')}</div>
                    <div className="text-sm text-muted-foreground">{t('settings.bitcoin_address')}</div>
                    <div className="font-mono text-sm bg-background p-2 rounded border">
                      1BvBMSEYstWetqTFn5Au4m4GFg7xJaNVN2
                    </div>
                    <div className="text-lg font-bold text-orange-500">
                      {t('settings.amount')}: 0.00012 BTC
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('settings.transaction_id')}</label>
                  <input 
                    type="text" 
                    placeholder={t('settings.enter_transaction_hash')}
                    className="w-full p-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Button 
                variant="romantic" 
                className="w-full"
                onClick={handleCompletePayment}
              >
                <span className="mr-2">✅</span>
                {t('settings.complete_payment')}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowPaymentFormModal(false);
                setShowPaymentModal(true);
              }}>
                {t('settings.back_to_payment_method')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Payment Modal */}
      <SubscriptionPaymentModal
        open={showSubscriptionPaymentModal}
        onOpenChange={setShowSubscriptionPaymentModal}
        selectedPlan={selectedPlan}
        onPurchaseSubscription={handlePurchaseSubscription}
      />


    </div>
  );
};